import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import { CircularProgress } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import ArgonInput from "components/ArgonInput";
import api from "utils/api";
import { toast } from "react-toastify";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import ProposalPlanDialog from "./components/ProposalPlanDialog";
import ProposalPlanTableData from "Views/UsefulCorners/PlanManagement/data/ProposalPlanTableData";

function PlanManagement() {
  const [proposalPlans, setProposalPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [proposalDialogOpen, setProposalDialogOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProposalId, setSelectedProposalId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Fetch proposal plans
  const fetchProposalPlans = async () => {
    setLoading(true);
    try {
      const response = await api.get("/planwithstudentreview", {
        params: { includeInactive: true },
      });
      if (response.data.success) {
        setProposalPlans(response.data.data?.proposalPlan || []);
      }
    } catch (error) {
      console.error("Error fetching proposal plans:", error);
      toast.error(error.response?.data?.message || "Failed to fetch proposal plans");
      setProposalPlans([]);
    } finally {
      // Add a small delay to show loading state
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    fetchProposalPlans();
  }, []);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Proposal Plan handlers
  const handleCreateProposal = () => {
    setSelectedProposal(null);
    setIsEditMode(false);
    setProposalDialogOpen(true);
  };

  const handleEditProposal = (proposal) => {
    setSelectedProposal(proposal);
    setIsEditMode(true);
    setProposalDialogOpen(true);
  };

  const handleProposalDialogClose = () => {
    setProposalDialogOpen(false);
    setSelectedProposal(null);
    setIsEditMode(false);
  };

  const handleProposalSubmit = async (proposalData) => {
    setLoading(true);
    try {
      let response;
      if (isEditMode) {
        response = await api.put(
          `/planwithstudentreview/proposal/${selectedProposal._id}`,
          proposalData
        );
        toast.success(response.data.message || "Proposal plan updated successfully!");
      } else {
        response = await api.post("/planwithstudentreview/proposal", proposalData);
        toast.success(response.data.message || "Proposal plan created successfully!");
      }
      fetchProposalPlans();
      handleProposalDialogClose();
    } catch (error) {
      console.error("Error saving proposal plan:", error);
      toast.error(error.response?.data?.message || "Failed to save proposal plan");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProposal = (proposalId) => {
    setSelectedProposalId(proposalId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedProposalId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedProposalId) return;

    setLoading(true);
    try {
      const response = await api.delete(`/planwithstudentreview/proposal/${selectedProposalId}`);
      if (response.data.success) {
        toast.success(response.data.message || "Proposal plan deleted successfully!");
        fetchProposalPlans();
        handleDeleteDialogClose();
      }
    } catch (error) {
      console.error("Error deleting proposal plan:", error);
      toast.error(error.response?.data?.message || "Failed to delete proposal plan");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (proposal) => {
    setLoading(true);
    try {
      const response = await api.put(`/planwithstudentreview/proposal/${proposal._id}`, {
        isActive: !(proposal.isActive !== false),
      });
      if (response.data.success) {
        toast.success(
          `${proposal.title} plan ${
            proposal.isActive !== false ? "deactivated" : "activated"
          } successfully!`
        );
        fetchProposalPlans();
      }
    } catch (error) {
      console.error("Error updating proposal plan status:", error);
      toast.error(error.response?.data?.message || "Failed to update proposal plan status");
    } finally {
      setLoading(false);
    }
  };

  // Filter proposal plans based on search
  const filteredProposals = proposalPlans.filter(
    (proposal) =>
      proposal.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      proposal.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const { columns: proposalColumns, rows: proposalRows } = ProposalPlanTableData(
    filteredProposals,
    handleEditProposal,
    handleDeleteProposal,
    handleToggleActive,
    loading
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
                flexWrap="wrap"
                gap={2}
              >
                <ArgonTypography variant="h6">Purchase Plan</ArgonTypography>

                <ArgonBox display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <ArgonInput
                    placeholder="Search by title or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ minWidth: "250px", flex: 1 }}
                  />
                  <ArgonButton
                    variant="contained"
                    color="primary"
                    onClick={handleCreateProposal}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    Add Proposal Plan
                  </ArgonButton>
                </ArgonBox>
              </ArgonBox>

              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Fetching Proposal Plan ..
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Please wait while we load the latest data.
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <ArgonBox>
                  {filteredProposals.length === 0 ? (
                    <ArgonBox p={3} textAlign="center">
                      <ArgonTypography variant="body2" color="text">
                        No proposal plans found
                      </ArgonTypography>
                    </ArgonBox>
                  ) : (
                    <ArgonBox
                      sx={{
                        "& .MuiTableRow-root": {
                          minHeight: "60px", // Increased row height
                        },
                        "& .MuiTableRow-root:not(:last-child)": {
                          "& td": {
                            borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                              `${borderWidth[1]} solid ${borderColor}`,
                          },
                        },
                      }}
                    >
                      <Table columns={proposalColumns} rows={proposalRows} />
                    </ArgonBox>
                  )}
                </ArgonBox>
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>

      {/* Proposal Plan Dialog */}
      <ProposalPlanDialog
        open={proposalDialogOpen}
        onClose={handleProposalDialogClose}
        proposal={selectedProposal}
        onSave={handleProposalSubmit}
        loading={loading}
        isEdit={isEditMode}
      />

      {/* Delete Proposal Dialog */}
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onConfirm={handleDeleteConfirm}
        title="Delete Proposal Plan"
        description="Are you sure you want to delete this proposal plan? This action cannot be undone."
        confirmText="Delete Proposal Plan"
        cancelText="Cancel"
      />

      <Footer />
    </DashboardLayout>
  );
}

export default PlanManagement;
